import { z } from 'zod';

// Base address interface with all common fields
export interface BaseAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// User address interface (simpler version)
export interface UserAddress extends BaseAddress {
  apartment?: string;
  unit?: string;
}

// Organization address interface (more detailed)
export interface OrganizationAddress extends BaseAddress {
  apartment?: string;
  unit?: string;
  building?: string;
  floor?: string;
}

// Validation schemas
export const baseAddressSchema = z.object({
  street: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State/Province is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
});

export const userAddressSchema = baseAddressSchema.extend({
  apartment: z.string().optional(),
  unit: z.string().optional(),
});

export const organizationAddressSchema = baseAddressSchema.extend({
  apartment: z.string().optional(),
  unit: z.string().optional(),
  building: z.string().optional(),
  floor: z.string().optional(),
});

// Helper functions
export const formatAddress = (address: BaseAddress): string => {
  const parts = [
    address.street,
    address.city,
    address.state,
    address.postalCode,
    address.country
  ].filter(Boolean);
  
  return parts.join(', ');
};

export const formatFullAddress = (address: OrganizationAddress): string => {
  const parts = [
    address.street,
    address.apartment && `Apt ${address.apartment}`,
    address.unit && `Unit ${address.unit}`,
    address.building && `Building ${address.building}`,
    address.floor && `Floor ${address.floor}`,
    address.city,
    address.state,
    address.postalCode,
    address.country
  ].filter(Boolean);
  
  return parts.join(', ');
};

export const validateAddress = (address: BaseAddress): boolean => {
  try {
    baseAddressSchema.parse(address);
    return true;
  } catch {
    return false;
  }
}; 